import type {
  EvidenceQuote,
  PainPoint,
  Report,
} from "@/orchestrator/types/report";
import type { JobCreateResponse, JobResponse } from "@/orchestrator/jobs/types";
import type { ThresholdLevel } from "@/orchestrator/validation/threshold-gate";

export const DEMO_TIME_WINDOW = {
  WEEK: 7,
  MONTH: 30,
  QUARTER: 90,
} as const;

export type DemoTimeWindowDays =
  (typeof DEMO_TIME_WINDOW)[keyof typeof DEMO_TIME_WINDOW];

export const DEMO_FLOW_STAGE = {
  IDLE: "idle",
  SAMPLE_READY: "sample_ready",
  RUNNING: "running",
  LIVE_READY: "live_ready",
  ERROR: "error",
} as const;

export type DemoFlowStage =
  (typeof DEMO_FLOW_STAGE)[keyof typeof DEMO_FLOW_STAGE];

export const DEMO_RESULT_KIND = {
  SAMPLE: "sample",
  LIVE: "live",
} as const;

export type DemoResultKind =
  (typeof DEMO_RESULT_KIND)[keyof typeof DEMO_RESULT_KIND];

export const DEMO_EVENT_NAME = {
  IMPRESSION: "landing_demo_impression",
  RUN_CLICKED: "landing_demo_run_clicked",
  JOB_ACCEPTED: "landing_demo_job_accepted",
  RESULT_SHOWN: "landing_demo_result_shown",
  ERROR: "landing_demo_error",
  CTA_OPENED: "landing_demo_cta_opened",
  BUILD_PROMPT_OPENED: "landing_build_prompt_opened",
  BUILD_PROMPT_COPIED: "landing_build_prompt_copied",
} as const;

export type DemoEventName =
  (typeof DEMO_EVENT_NAME)[keyof typeof DEMO_EVENT_NAME];

export interface DemoInput {
  nicheSlug: string;
  timeWindowDays: DemoTimeWindowDays;
  forceRefresh?: boolean;
}

export interface DemoPreviewEvidence {
  id: string;
  text: string;
  author: string;
  commenter?: string;
  community?: string;
  platform: EvidenceQuote["platform"];
  sourceLabel?: string;
  sourceUrl?: string;
  permalink?: string;
}

export interface DemoPreviewPainPoint {
  id: string;
  title: string;
  description: string;
  severityScore: number;
  frequency: number;
  category: PainPoint["category"];
  evidence: DemoPreviewEvidence[];
}

export interface DemoGeneratedIdea {
  id: string;
  title: string;
  whyNow: string;
  fitScore: number;
  // Strengthened per Phase 2 spec
  description?: string;
  problemItSolves?: string;
  solutionApproach?: string;
  relevanceScore?: number;
  status?: "draft" | "validated" | "archived";
}

export interface DemoIdealCustomer {
  id: string;
  segment: string;
  pains: string[];
  urgency: "low" | "medium" | "high";
  // Strengthened per Phase 2 spec (all already present in sample data, now explicit)
  role?: string;
  industry?: string;
  frustrators?: string[];
  goals?: string[];
  budget?: string;
  timeline?: string;
}

export interface DemoValidatorScore {
  consistency: number;
  confidence: "low" | "medium" | "high";
  severityIndex: number;
  rationale: string[];
}

export interface DemoValidatorContext {
  score: DemoValidatorScore;
  derivedAt: string;
}

export const DEMO_DETAIL_TABS = {
  OVERVIEW: "overview",
  PAIN_POINTS: "pain-points",
  CUSTOMERS: "customers",
  VALIDATION: "validation",
  ACTIONS: "actions",
} as const;

export type DemoDetailTab =
  (typeof DEMO_DETAIL_TABS)[keyof typeof DEMO_DETAIL_TABS];

export interface DemoInteractionState {
  nicheSlug: string;
  savedPainPointIds: string[];
  selectedIdeaId?: string;
  lastActiveTab?: DemoDetailTab;
  momTestAnswers?: Record<string, string>;
}

export interface DemoPreview {
  reportId?: string;
  nicheSlug: string;
  timeWindowDays: number;
  summary: string;
  validationLevel?: ThresholdLevel;
  consistencyScore?: number;
  methodology?: string;
  confidenceDisclaimer?: string;
  postsCollected?: number;
  painPoints: DemoPreviewPainPoint[];
  recommendations: string[];
  generatedIdeas?: DemoGeneratedIdea[];
  idealCustomers?: DemoIdealCustomer[];
  landingPageFeaturePrompts?: string[];
  validatorContext?: DemoValidatorContext;
  contentState?: DemoPreviewContentState;
  kind: DemoResultKind;
}

export interface DemoTelemetrySnapshot {
  progress?: number;
  currentStep?: string;
  durationMs?: number;
}

export interface DemoIdleState {
  stage: typeof DEMO_FLOW_STAGE.IDLE;
  defaults: DemoInput;
}

export interface DemoSampleReadyState {
  stage: typeof DEMO_FLOW_STAGE.SAMPLE_READY;
  preview: DemoPreview;
}

export interface DemoRunningState {
  stage: typeof DEMO_FLOW_STAGE.RUNNING;
  input: DemoInput;
  jobId?: string;
  telemetry?: DemoTelemetrySnapshot;
}

export interface DemoLiveReadyState {
  stage: typeof DEMO_FLOW_STAGE.LIVE_READY;
  input: DemoInput;
  preview: DemoPreview;
  telemetry?: DemoTelemetrySnapshot;
}

export interface DemoErrorState {
  stage: typeof DEMO_FLOW_STAGE.ERROR;
  input: DemoInput;
  code: string;
  message: string;
  recoverable: boolean;
}

export type DemoFlowState =
  | DemoIdleState
  | DemoSampleReadyState
  | DemoRunningState
  | DemoLiveReadyState
  | DemoErrorState;

export interface DemoAnalyticsEvent {
  name: DemoEventName;
  nicheSlug?: string;
  timeWindowDays?: number;
  resultKind?: DemoResultKind;
  durationBucket?: string;
  source?: string;
}

export interface DemoJobAccepted {
  jobId: JobCreateResponse["jobId"];
  status: JobCreateResponse["status"];
  pollUrl: JobCreateResponse["pollUrl"];
}

export interface DemoJobProgress {
  jobId: JobResponse["jobId"];
  status: JobResponse["status"];
  progress?: number;
  currentStep?: string;
}

export interface DemoLiveResult {
  jobId: JobResponse["jobId"];
  preview: DemoPreview;
  telemetry?: DemoTelemetrySnapshot;
}

export interface DemoReportSource {
  report?: Report | null;
  nicheSlug?: string;
  timeWindowDays?: number;
}

// ─── Content state availability metadata ────────────────────────────────────

export type DemoContentAvailability = "sample" | "grounded" | "unavailable";

export interface DemoPreviewContentState {
  generatedIdeas?: DemoContentAvailability;
  idealCustomers?: DemoContentAvailability;
  landingPageFeaturePrompts?: DemoContentAvailability;
  validatorScore?: "grounded" | "derived";
}

// ─── Validation score iteration types ────────────────────────────────────────

export type DemoValidationMessageType = "warning" | "suggestion" | "positive";

export interface DemoValidationMessage {
  type: DemoValidationMessageType;
  text: string;
}

export interface MockEvaluationResult {
  score: number; // 0..10 integer
  tone: "critical" | "concerned" | "neutral" | "encouraging";
  messages: DemoValidationMessage[];
}
